import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { 
  Users, TrendingUp, Languages, Clock, Activity, 
  RefreshCw, Download, AlertTriangle, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function UserAnalytics({ adminToken }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [overview, setOverview] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [userUsage, setUserUsage] = useState([]);
  const [languageStats, setLanguageStats] = useState([]);
  const [lowBalanceUsers, setLowBalanceUsers] = useState([]);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${adminToken}` }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, dailyRes, userRes, langRes, lowBalRes] = await Promise.all([
        axios.get(`${API}/api/admin/analytics/overview`, axiosConfig),
        axios.get(`${API}/api/admin/analytics/usage/daily?days=30`, axiosConfig),
        axios.get(`${API}/api/admin/analytics/usage/by-user?period=${period}&limit=20`, axiosConfig),
        axios.get(`${API}/api/admin/analytics/usage/by-language?period=${period}`, axiosConfig),
        axios.get(`${API}/api/admin/analytics/users/low-balance?threshold=100`, axiosConfig)
      ]);

      // Normalize: old endpoint returns flat {total_users, active_users_7d, ...}
      // v2 endpoint returns nested {users:{total,active_this_week,...}, translations:{...}, plan_distribution:{...}}
      // Support both by normalizing to nested structure
      const raw = overviewRes.data;
      const normalized = raw.users ? raw : {
        users: {
          total: raw.total_users || 0,
          new_this_week: raw.new_users_this_week || 0,
          active_this_week: raw.active_users_7d || 0,
        },
        translations: {
          total_sessions: raw.total_translations || 0,
          total_words: raw.total_words_translated || 0,
        },
        plan_distribution: raw.users_by_plan || {},
      };
      setOverview(normalized);
      setDailyUsage(dailyRes.data);
      setUserUsage(userRes.data);
      setLanguageStats(langRes.data);
      setLowBalanceUsers(lowBalRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-900">User Analytics</h1>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="quarter">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Users className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Users</p>
                <p className="text-2xl font-bold text-zinc-900">{formatNumber(overview.users?.total)}</p>
                <p className="text-xs text-emerald-600">+{overview.users?.new_this_week} this week</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Activity className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Active Users</p>
                <p className="text-2xl font-bold text-zinc-900">{formatNumber(overview.users?.active_this_week)}</p>
                <p className="text-xs text-zinc-500">Last 7 days</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Languages className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Translations</p>
                <p className="text-2xl font-bold text-zinc-900">{formatNumber(overview.translations?.total_sessions)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Words</p>
                <p className="text-2xl font-bold text-zinc-900">{formatNumber(overview.translations?.total_words)}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Usage Chart */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Daily Usage Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyUsage}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="words" 
                stroke="#6366f1" 
                fill="#6366f1" 
                fillOpacity={0.2}
                name="Words"
              />
              <Area 
                type="monotone" 
                dataKey="translations" 
                stroke="#22c55e" 
                fill="#22c55e" 
                fillOpacity={0.2}
                name="Translations"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Plan Distribution Pie Chart */}
        {overview?.plan_distribution && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">User Plan Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(overview.plan_distribution).map(([plan, count]) => ({
                    name: plan || 'Free',
                    value: count
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  dataKey="value"
                >
                  {Object.entries(overview.plan_distribution).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Language Stats */}
      {languageStats.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Translation by Language Pair</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={languageStats.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                type="category" 
                dataKey="source_language" 
                width={100}
                tick={{ fontSize: 12 }}
                tickFormatter={(val, idx) => {
                  const item = languageStats[idx];
                  return item ? `${item.source_language} → ${item.target_language}` : val;
                }}
              />
              <Tooltip />
              <Bar dataKey="words" fill="#6366f1" name="Words" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Top Users Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Top Users by Word Count</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Plan</th>
                <th className="text-right p-2">Translations</th>
                <th className="text-right p-2">Words</th>
                <th className="text-right p-2">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {userUsage.map((user, idx) => (
                <tr key={idx} className="border-b hover:bg-zinc-50">
                  <td className="p-2">
                    <div>
                      <p className="font-medium">{user.name || 'N/A'}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.plan === 'business' ? 'bg-purple-100 text-purple-700' :
                      user.plan === 'professional' ? 'bg-indigo-100 text-indigo-700' :
                      user.plan === 'starter' ? 'bg-blue-100 text-blue-700' :
                      user.plan === 'payg' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-zinc-100 text-zinc-700'
                    }`}>
                      {user.plan || 'Free'}
                    </span>
                  </td>
                  <td className="p-2 text-right">{formatNumber(user.total_translations)}</td>
                  <td className="p-2 text-right font-medium">{formatNumber(user.total_words)}</td>
                  <td className="p-2 text-right text-zinc-500 text-xs">
                    {user.last_activity ? new Date(user.last_activity).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Low Balance Alert */}
      {lowBalanceUsers.count > 0 && (
        <Card className="p-6 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-amber-800">
              Low Balance Alert ({lowBalanceUsers.count} users)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-200">
                  <th className="text-left p-2">User</th>
                  <th className="text-right p-2">Wallet</th>
                  <th className="text-right p-2">Credits</th>
                  <th className="text-left p-2">Plan</th>
                </tr>
              </thead>
              <tbody>
                {lowBalanceUsers.users?.slice(0, 10).map((user, idx) => (
                  <tr key={idx} className="border-b border-amber-100">
                    <td className="p-2">
                      <p className="font-medium">{user.name || 'N/A'}</p>
                      <p className="text-xs text-amber-700">{user.email}</p>
                    </td>
                    <td className="p-2 text-right font-bold text-emerald-700">
                      ₹{user.wallet_balance || 0}
                    </td>
                    <td className="p-2 text-right text-amber-800">
                      {user.credits_balance}
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.plan === 'payg' ? 'bg-emerald-100 text-emerald-700' :
                        user.plan === 'starter' ? 'bg-blue-100 text-blue-700' :
                        'bg-zinc-100 text-zinc-700'
                      }`}>
                        {user.plan || 'Free'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
